/**
 * The sections of a framework's page, in the findings queue's idiom: a
 * hairline card per section, dense rows, a count beside each title. The
 * route composes them; each takes only what it draws.
 *
 * @module components/regulation/frameworks/FrameworkDetailSections
 */
import { Link } from "@tanstack/react-router";
import { ChevronRightIcon, ExternalLinkIcon, FileTextIcon } from "lucide-react";
import type { ReactNode } from "react";

import { cn } from "~/lib/utils";
import type { FrameworkDocumentRef, GovernanceFramework } from "../../../state/governance";
import type { RegulatoryUpdate } from "../../../state/policies";
import { safeExternalHref } from "../../../lib/safeExternalHref";
import type { FindingItem } from "../finding/findingItem";
import { SeverityGlyph } from "../finding/findingGlyphs";
import { publicationImpact, WeeklyChart, weeklySeries } from "../monitor/MonitorSection";
import { isQuiet, relative } from "../monitor/publications";
import { CoverageMeter, RequirementGlyph } from "./FrameworkGlyphs";
import { REQUIREMENT_STATUS_LABEL, type RequirementRow } from "./frameworkView";

export const SECTION_CARD = "overflow-hidden rounded-xl border border-border/60 bg-card";
const ROW = "flex min-w-0 items-center gap-3 px-4 text-[13px] transition-colors duration-150";
const ROW_LINK = cn(ROW, "outline-none hover:bg-foreground/[0.03] focus-visible:bg-foreground/[0.05]");

const shortDate = (value: string): string =>
  new Date(value).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });

export function Section({
  title,
  count,
  aside,
  children,
  className,
  id,
}: {
  readonly title: string;
  readonly count?: number | undefined;
  readonly aside?: ReactNode;
  readonly children: ReactNode;
  readonly className?: string;
  readonly id?: string;
}) {
  const headingId = id ? `${id}-heading` : undefined;
  return (
    <section id={id} aria-labelledby={headingId} className={cn("min-w-0 scroll-mt-6", className)}>
      <div className="mb-2 flex min-h-6 items-center justify-between gap-3">
        <h2 id={headingId} className="flex items-baseline gap-2 text-[13px] font-semibold text-foreground">
          {title}
          {count !== undefined ? <span className="text-xs font-normal tabular-nums text-muted-foreground">{count}</span> : null}
        </h2>
        {aside ? <div className="flex shrink-0 items-center gap-2 text-xs text-muted-foreground">{aside}</div> : null}
      </div>
      {children}
    </section>
  );
}

/** A quiet placeholder inside a section, the same height whichever section it sits in. */
export function SectionEmpty({ children, tone = "muted" }: { readonly children: ReactNode; readonly tone?: "muted" | "warn" }) {
  return (
    <p
      className={cn(
        "rounded-xl border border-dashed border-border/70 px-4 py-4 text-[13px]",
        tone === "warn" ? "text-warning" : "text-muted-foreground",
      )}
    >
      {children}
    </p>
  );
}

/** A section's "see all" link: quiet until hovered. */
export const SECTION_LINK =
  "inline-flex items-center gap-0.5 rounded-sm font-medium text-muted-foreground outline-none hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring";

/* ------------------------------------------------------------------ */
/* Requirements                                                        */
/* ------------------------------------------------------------------ */

export function RequirementsSection({
  rows,
  orgSegment,
}: {
  readonly rows: ReadonlyArray<RequirementRow>;
  readonly orgSegment: string;
}) {
  const satisfied = rows.reduce((sum, row) => sum + row.coverage.satisfied, 0);
  const total = rows.reduce((sum, row) => sum + row.coverage.total, 0);
  return (
    <Section
      id="requirements"
      title="Requirements"
      count={rows.length}
      aside={total > 0 ? <span className="tabular-nums">{satisfied} of {total} controls addressed</span> : undefined}
    >
      {rows.length === 0 ? (
        <SectionEmpty>It answers to no regime yet. Edit the framework to choose the regimes it organises.</SectionEmpty>
      ) : (
        <ul className={cn(SECTION_CARD, "divide-y divide-border/50")}>
          {rows.map((row) => (
            <li key={row.id}>
              <Link
                to="/$org/compliance/regimes/$regimeId"
                params={{ org: orgSegment, regimeId: row.id }}
                className={cn(ROW_LINK, "group/row h-14")}
              >
                <RequirementGlyph status={row.status} coverage={row.coverage} />
                <span className="min-w-0 flex-1">
                  <span className="block truncate font-medium text-foreground" title={row.name}>
                    {row.name}
                  </span>
                  {row.regulator || row.jurisdiction ? (
                    <span className="mt-0.5 block truncate text-xs text-muted-foreground">
                      {[row.regulator, row.jurisdiction].filter(Boolean).join(" · ")}
                    </span>
                  ) : null}
                </span>
                <CoverageMeter coverage={row.coverage} showCount className="w-40 shrink-0 max-sm:hidden" barClassName="w-20" />
                <span
                  className={cn(
                    "w-20 shrink-0 text-right text-xs",
                    row.status === "covered" ? "text-success" : row.status === "uncatalogued" ? "text-muted-foreground/70" : "text-muted-foreground",
                  )}
                >
                  {REQUIREMENT_STATUS_LABEL[row.status]}
                </span>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </Section>
  );
}

/* ------------------------------------------------------------------ */
/* Findings                                                            */
/* ------------------------------------------------------------------ */

export const FINDINGS_SHOWN = 6;

export function FindingsSection({
  items,
  ready,
  documentCount,
  orgSegment,
  now,
}: {
  readonly items: ReadonlyArray<FindingItem>;
  readonly ready: boolean;
  readonly documentCount: number;
  readonly orgSegment: string;
  readonly now: number;
}) {
  const shown = items.slice(0, FINDINGS_SHOWN);
  return (
    <Section
      id="findings"
      title="Open on its documents"
      count={ready ? items.length : undefined}
      aside={
        <Link to="/$org/compliance/findings" params={{ org: orgSegment }} className={SECTION_LINK}>
          All findings
          <ChevronRightIcon aria-hidden className="size-3" />
        </Link>
      }
    >
      {!ready ? (
        <div className={cn(SECTION_CARD, "divide-y divide-border/50")} aria-busy="true" aria-label="Loading findings">
          {[0, 1].map((index) => (
            <div key={index} className={cn(ROW, "h-11")}>
              <span className="size-4 animate-pulse rounded bg-muted" />
              <span className="h-3 w-2/3 animate-pulse rounded bg-muted" />
            </div>
          ))}
        </div>
      ) : items.length === 0 ? (
        <SectionEmpty>
          {documentCount === 0 ? "No documents are filed under it, so nothing can be found against them." : "Nothing open against its documents."}
        </SectionEmpty>
      ) : (
        <ul className={cn(SECTION_CARD, "divide-y divide-border/50")}>
          {shown.map((item) => (
            <li key={item.id}>
              <Link
                to="/$org/compliance/findings/$findingId"
                params={{ org: orgSegment, findingId: item.id }}
                className={cn(ROW_LINK, "h-11")}
              >
                <SeverityGlyph severity={item.severity} />
                <span className="min-w-0 flex-1 truncate font-medium text-foreground" title={item.title}>
                  {item.title}
                </span>
                <span className="hidden max-w-48 items-center gap-1 truncate rounded-md border border-border/60 px-1.5 py-0.5 text-xs text-muted-foreground md:inline-flex" title={item.documentName}>
                  <FileTextIcon aria-hidden className="size-3 shrink-0" />
                  <span className="truncate">{item.documentName}</span>
                </span>
                <time className="w-24 shrink-0 whitespace-nowrap text-right text-xs tabular-nums text-muted-foreground" dateTime={new Date(item.raisedAt).toISOString()}>
                  {relative(new Date(item.raisedAt).toISOString(), now)}
                </time>
              </Link>
            </li>
          ))}
          {items.length > shown.length ? (
            <li className="px-4 py-2 text-xs text-muted-foreground">
              {items.length - shown.length} more in{" "}
              <Link to="/$org/compliance/findings" params={{ org: orgSegment }} className="font-medium text-foreground/85 hover:underline">
                Findings
              </Link>
            </li>
          ) : null}
        </ul>
      )}
    </Section>
  );
}

/* ------------------------------------------------------------------ */
/* Documents                                                           */
/* ------------------------------------------------------------------ */

const KIND_TAGS = { policy: "Policy", procedure: "Procedure", framework: "Framework" } as const;

export function DocumentsSection({
  documents,
  openByDocument,
  orgSegment,
}: {
  readonly documents: ReadonlyArray<FrameworkDocumentRef>;
  readonly openByDocument: ReadonlyMap<string, number>;
  readonly orgSegment: string;
}) {
  return (
    <Section id="documents" title="Documents" count={documents.length}>
      {documents.length === 0 ? (
        <SectionEmpty>
          Nothing filed under it yet. Set a document's Framework field on its own page, or accept one of the inspector's
          suggestions.
        </SectionEmpty>
      ) : (
        <ul className={cn(SECTION_CARD, "divide-y divide-border/50")}>
          {documents.map((document) => {
            const open = openByDocument.get(document.id) ?? 0;
            return (
              <li key={document.id}>
                <Link to="/$org/compliance/policies/$policyId" params={{ org: orgSegment, policyId: document.id }} className={cn(ROW_LINK, "h-11")}>
                  <FileTextIcon aria-hidden className="size-4 shrink-0 text-muted-foreground" />
                  <span className="min-w-0 flex-1 truncate font-medium text-foreground" title={document.title ?? document.filename}>
                    {document.title ?? document.filename}
                  </span>
                  {open > 0 ? (
                    <span className="flex shrink-0 items-center gap-1.5 text-xs tabular-nums text-muted-foreground" title={`${open} open ${open === 1 ? "finding" : "findings"}`}>
                      <span aria-hidden className="size-2 rounded-full bg-orange-500 dark:bg-orange-400" />
                      {open}
                    </span>
                  ) : null}
                  <span className="w-20 shrink-0 text-right text-xs text-muted-foreground">{KIND_TAGS[document.documentKind]}</span>
                </Link>
              </li>
            );
          })}
        </ul>
      )}
    </Section>
  );
}

/* ------------------------------------------------------------------ */
/* Publications                                                        */
/* ------------------------------------------------------------------ */

export function PublicationsSection({
  publications,
  orgSegment,
}: {
  readonly publications: ReadonlyArray<RegulatoryUpdate>;
  readonly orgSegment: string;
}) {
  return (
    <Section
      id="publications"
      title="Recent publications"
      aside={
        <Link to="/$org/compliance/regulatory" params={{ org: orgSegment }} className={SECTION_LINK}>
          All publications
          <ChevronRightIcon aria-hidden className="size-3" />
        </Link>
      }
    >
      {publications.length === 0 ? (
        <SectionEmpty>Nothing recorded against its regimes yet.</SectionEmpty>
      ) : (
        <ul className={cn(SECTION_CARD, "divide-y divide-border/50")}>
          {publications.map((update) => {
            const impact = publicationImpact(update);
            const href = safeExternalHref(update.sourceUrl);
            return (
              <li key={update.id} className={cn(ROW, "h-11")}>
                <span className="w-24 shrink-0 whitespace-nowrap text-xs tabular-nums text-muted-foreground">
                  {shortDate(update.publishedAt ?? update.detectedAt)}
                </span>
                <span className="min-w-0 flex-1 truncate text-foreground" title={update.title}>
                  {update.title}
                </span>
                {href ? (
                  <a
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={`Open ${update.title} at the source`}
                    className="shrink-0 rounded-sm text-muted-foreground outline-none hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring"
                  >
                    <ExternalLinkIcon className="size-3.5" />
                  </a>
                ) : null}
                <span className={cn("shrink-0 rounded-full px-2 py-0.5 text-[11px] font-medium max-sm:hidden", impact.className)}>{impact.label}</span>
              </li>
            );
          })}
        </ul>
      )}
    </Section>
  );
}

/* ------------------------------------------------------------------ */
/* Rail                                                                */
/* ------------------------------------------------------------------ */

/** A label and its value, as a property list in a side rail. */
export function Property({ label, children }: { readonly label: string; readonly children: ReactNode }) {
  return (
    <div className="grid grid-cols-[6.5rem_minmax(0,1fr)] items-start gap-2 py-1.5 text-[13px]">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="min-w-0 text-foreground">{children}</dd>
    </div>
  );
}

export function ActivityCard({
  publications,
  decisionsOwed,
  thisWeek,
  now,
}: {
  readonly publications: ReadonlyArray<RegulatoryUpdate>;
  readonly decisionsOwed: number;
  readonly thisWeek: number;
  readonly now: number;
}) {
  return (
    <Section title="Activity" aside={<span>last twelve weeks</span>}>
      <div className={SECTION_CARD}>
        <div className="grid grid-cols-2 gap-px bg-border/50">
          <div className="bg-card px-4 py-3">
            <p className={cn("text-lg font-semibold tabular-nums", decisionsOwed > 0 ? "text-warning" : "text-foreground")}>{decisionsOwed}</p>
            <p className="text-xs text-muted-foreground">{decisionsOwed === 1 ? "publication needs a decision" : "publications need a decision"}</p>
          </div>
          <div className="bg-card px-4 py-3">
            <p className="text-lg font-semibold tabular-nums text-foreground">{thisWeek}</p>
            <p className="text-xs text-muted-foreground">published this week</p>
          </div>
        </div>
        <div className="border-t border-border/50 px-2 pt-2">
          {publications.length === 0 ? (
            <p className="flex h-24 items-center justify-center px-4 text-center text-xs text-muted-foreground">
              No publications recorded against its regimes yet.
            </p>
          ) : (
            <div className="h-24">
              <WeeklyChart data={weeklySeries(publications, now)} />
            </div>
          )}
        </div>
      </div>
    </Section>
  );
}

export function SourcesCard({
  sources,
  now,
}: {
  readonly sources: GovernanceFramework["sources"];
  readonly now: number;
}) {
  const healthy = sources.filter((source) => source.enabled && !isQuiet(source, now)).length;
  return (
    <Section title="Watched by" aside={sources.length > 0 ? <span className="tabular-nums">{healthy}/{sources.length} read this fortnight</span> : undefined}>
      {sources.length === 0 ? (
        <SectionEmpty tone="warn">Nobody. Watch a source on the Monitor and its regimes' publications land here.</SectionEmpty>
      ) : (
        <ul className={cn(SECTION_CARD, "divide-y divide-border/50")}>
          {sources.map((source) => {
            const quiet = isQuiet(source, now);
            return (
              <li key={source.id} className={cn(ROW, "h-10")}>
                <span
                  aria-hidden
                  className={cn("size-1.5 shrink-0 rounded-full", !source.enabled ? "bg-muted-foreground/40" : quiet ? "bg-warning" : "bg-success")}
                />
                <span className="min-w-0 flex-1 truncate text-foreground" title={source.name}>
                  {source.name}
                </span>
                <span className={cn("shrink-0 text-xs", quiet ? "text-warning" : "text-muted-foreground")}>
                  {source.enabled ? relative(source.lastCheckedAt, now) : "off"}
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </Section>
  );
}
